import FormContainer from '../../components/form/FormContainer';
import { useState, useEffect } from 'react';
import { Button, Form, InputGroup, Card } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Loader from '../../components/layout/Loader';
import Meta from '../../components/layout/Meta';
import { useLoginMutation } from '../../slices/userApiSlice';
import { setCredentials } from '../../slices/authSlice';
import { fetchActiveGlobalDiscount } from '../../slices/activeGlobalDiscountSlice';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash, FaUserCircle } from 'react-icons/fa';
import { clearGuestId } from '../../utils/sessionUtils';
import { orderApiSlice } from '../../slices/orderApiSlice';
import { cartApiSlice } from '../../slices/cartApiSlice';

const LoginScreen = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [keepLoggedIn, setKeepLoggedIn] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [login, { isLoading }] = useLoginMutation();

    const { userInfo } = useSelector((state) => state.auth);

    const { search } = useLocation();
    const searchParams = new URLSearchParams(search);
    const redirect = searchParams.get('redirect') || '/';

    useEffect(() => {
        if (userInfo) {
            navigate(redirect);
        }
    }, [userInfo, redirect, navigate]);

    const submitHandler = async (e) => {
        e.preventDefault();
        try {
            // Get device/browser info (user-agent)
            const deviceInfo = navigator.userAgent;
            const res = await login({
                email,
                password,
                keepLoggedIn,
                deviceInfo,
            }).unwrap();
            dispatch(setCredentials({ ...res }));
            dispatch(fetchActiveGlobalDiscount());
            clearGuestId();
            // Force refetch of active order and cart immediately
            dispatch(orderApiSlice.util.invalidateTags(['Order']));
            dispatch(cartApiSlice.util.invalidateTags(['Cart']));
            dispatch(cartApiSlice.endpoints.getCart.initiate(undefined, { forceRefetch: true }));
            navigate(redirect);
        } catch (e) {
            toast.error(e?.data?.message || e.error);
        }
    };

    return (
        <FormContainer>
            <Meta title="Iniciar Sesión" />
            <Card className="shadow-lg border-0 rounded-4 mt-4">
                <Card.Body className="p-4 p-md-5">
                    <div className="text-center mb-4">
                        <div className="mb-3">
                            <FaUserCircle size={60} color="#b5430f" />
                        </div>
                        <h2 className="fw-bold text-dark">Iniciar Sesión</h2>
                        <p className="text-muted">Bienvenido de nuevo a HomeSanty</p>
                    </div>

                    <Form onSubmit={submitHandler}>
                        <Form.Group controlId="email" className="mb-4">
                            <Form.Label className="fw-semibold" column='lg'>Email o Documento de Identidad</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="ejemplo@correo.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="py-2 px-3 rounded-3 border-secondary-subtle"
                            ></Form.Control>
                        </Form.Group>

                        <Form.Group controlId="password" className="mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <Form.Label className="fw-semibold" column='lg'>Contraseña</Form.Label>
                                <Link 
                                    to="/forgot-password" 
                                    className="small text-decoration-none"
                                    style={{ color: '#b5430f' }}
                                >
                                    ¿Olvidaste tu contraseña?
                                </Link>
                            </div>
                            <InputGroup>
                                <Form.Control
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Ingrese su contraseña"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="py-2 px-3 rounded-start-3 border-secondary-subtle border-end-0"
                                ></Form.Control>
                                <Button
                                    variant="outline-secondary"
                                    className="border-secondary-subtle border-start-0 rounded-end-3 bg-white text-muted"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </Button>
                            </InputGroup>
                        </Form.Group>


                        <Form.Group controlId="keepLoggedIn" className="mb-4 d-flex align-items-center">
                            <Form.Check
                                type="checkbox"
                                id="keepLoggedIn"
                                checked={keepLoggedIn}
                                onChange={(e) => setKeepLoggedIn(e.target.checked)}
                                className="me-2"
                                style={{ accentColor: '#b5430f' }}
                                label={<span className="fw-semibold" style={{ color: '#b5430f' }}>Mantener sesión iniciada</span>}
                            />
                        </Form.Group>

                        <Button 
                            type="submit" 
                            variant="primary" 
                            className="w-100 py-2 rounded-3 fw-bold shadow-sm" 
                            disabled={isLoading}
                            style={{ backgroundColor: '#b5430f', borderColor: '#b5430f' }}
                        >
                            {isLoading ? 'Iniciando...' : 'Iniciar Sesión'}
                        </Button>

                        {isLoading && <div className="mt-3 text-center"><Loader /></div>}
                    </Form>

                    <div className="mt-4 text-center">
                        <p className="text-muted mb-2">¿No tienes una cuenta en HomeSanty?</p>
                        <Link
                            to={redirect ? `/register?redirect=${redirect}` : '/register'}
                            className="btn btn-outline-dark w-100 py-2 rounded-3 fw-semibold"
                        >
                            Crear Cuenta Nueva
                        </Link>
                    </div>
                </Card.Body>
            </Card>
        </FormContainer>
    );
};

export default LoginScreen;
